/**
 * Friendly-error mapping for the first-party login screen (controlplane-auth task 12.1; requirements
 * A1, A5). This echoes AgentDNA's `agentdna-dashboard/src/lib/authErrors.ts` — a small, pure map
 * from a server/transport outcome to a short, human sentence shown inline on the form — adapted to
 * THIS control plane's `/auth/*` endpoints and its {@link ApiError} classes.
 *
 * Why a dedicated map (and not just the raw server message):
 *  - The `/auth/*` handlers deliberately return OPAQUE outcomes so a probe cannot enumerate accounts
 *    (design §Error Handling / login.ts, email-otp.ts): a wrong password, an unknown email and a
 *    user with no password all collapse to one `invalid_credentials`; `/auth/start` and
 *    `/auth/forgot` always answer the same `accepted`. The UI must present an equally non-revealing,
 *    yet friendly, sentence — never a stack trace, never a code.
 *  - A locked identifier (429) should tell the operator to wait, optionally for how long
 *    (`Retry-After`), without implying the account exists.
 *  - A cold control plane (503 "waking") is a transient "try again", not a credential problem.
 *
 * The function is PURE and side-effect free so it is unit-testable on its own and reused by every
 * entry point (password+MFA, email OTP, OIDC) on the login screen.
 */

import { ApiError } from "../../lib/api.ts";

/**
 * A stable, closed set of friendly messages, keyed so a caller (or a test) can assert the exact
 * sentence without matching free text. Each value is a complete, user-facing sentence.
 */
export const AUTH_MESSAGES = {
  /** Wrong/absent credential, unknown email, or a wrong second factor — the one opaque failure. */
  invalid_credentials: "That email or password is incorrect. Please try again.",
  /** A supplied TOTP / recovery code did not verify. */
  invalid_code: "That code is incorrect or has expired. Please request a new one.",
  /** The account is temporarily locked after repeated failures (A7.4). */
  locked: "Too many attempts. Please wait a few minutes and try again.",
  /** The account exists but is suspended. */
  suspended: "This account is suspended. Contact your team admin.",
  /** Authenticated but has no team membership yet (403, A3.5). */
  not_provisioned: "You do not have access to a team yet. Ask an admin to invite you.",
  /** The SSO sign-in could not be completed (bad state/nonce/id_token, or no matching invite). */
  sso_failed: "Single sign-on could not be completed. Please try again or use another method.",
  /** The control plane is cold/unreachable (503) — transient. */
  waking: "The control plane is waking up. Please try again in a moment.",
  /** The request never reached the server (offline, DNS, abort). */
  network: "We could not reach the control plane. Check your connection and try again.",
  /** A malformed/unexpected server response. */
  malformed: "Something went wrong. Please try again.",
  /** A server-side (5xx) failure that is not the waking state. */
  server: "The control plane ran into a problem. Please try again shortly.",
  /** Any other request rejection (4xx) with no more specific mapping. */
  request: "That request could not be completed. Please check your details and try again.",
  /** A client-side validation failure before anything is sent (e.g. empty fields). */
  validation: "Please fill in all required fields.",
  /** A client-side check that the user-tab email is well formed, before any network request (A6.2). */
  invalid_email: "Please enter a valid email address.",
  /**
   * User-tab terminal result (per-team-signin-factors R8.6/R8.7/R10.1): the email names no team —
   * no domain-SSO match, or a matched team without an SSO provider. A hard stop with no login.
   */
  no_team: "This email is not part of any team.",
  /**
   * User-tab terminal result (per-team-signin-factors R7.6): the account's active teams present an
   * incoherent factor mix. A hard stop with no login until the configuration is corrected.
   */
  anomaly: "Your teams are configured inconsistently. Contact your team admin to resolve this before signing in.",
  /**
   * The account-banned terminal message (per-team-signin-factors R13.1/R13.2/R13.3). Shown AFTER
   * authentication (never before) when the sign-in is refused for a banned membership — for
   * `/login?sso=banned` (the SSO callback outcome) and for the `account_banned` result of any
   * credential path (password, TOTP, passkey, email code). Identical copy on both the user and admin
   * tabs; no session is held and no team is offered.
   */
  account_banned: "Your account has been banned. Contact your team admin.",
  /**
   * The account-exists terminal message (per-team-signin-factors R8, `/login?sso=account_exists`):
   * a domain-SSO first login whose email already has an account with no identity for this IdP. No
   * session is minted; the user is directed to their team admin.
   */
  account_exists: "An account already exists for this email. Contact your team admin.",
  /** A generic fallback. */
  unknown: "Something went wrong. Please try again.",
} as const;

/** The keys of {@link AUTH_MESSAGES} — the closed set of friendly-error kinds. */
export type AuthMessageKey = keyof typeof AUTH_MESSAGES;

/** A friendly error ready to render inline: a stable key plus its user-facing sentence. */
export interface FriendlyAuthError {
  readonly key: AuthMessageKey;
  readonly message: string;
}

/** Build a {@link FriendlyAuthError} from a key (so a caller can surface a client-side one too). */
export function friendly(key: AuthMessageKey): FriendlyAuthError {
  return { key, message: AUTH_MESSAGES[key] };
}

/**
 * Map a server-returned `error.code` (the auth endpoints' opaque outcome codes, carried in the
 * `{ error: { code } }` body `apiFetch`/`authFetch` surface) to a friendly key. Codes the login
 * flows may emit are covered; anything else falls through to `null` so the HTTP-status mapping in
 * {@link friendlyAuthError} decides.
 */
function codeToKey(code: string | null): AuthMessageKey | null {
  switch (code) {
    case "invalid_credentials":
      return "invalid_credentials";
    case "invalid_code":
      return "invalid_code";
    case "locked":
      return "locked";
    case "suspended":
      return "suspended";
    case "not_provisioned":
    case "forbidden":
      return "not_provisioned";
    case "sso_failed":
      return "sso_failed";
    default:
      return null;
  }
}

/**
 * Translate an {@link ApiError} (the only error type `apiFetch`/`authFetch` throw) into a friendly,
 * non-revealing message for the login form. The server `error.code` is preferred when it names a
 * known outcome; otherwise the error `kind` (which already classifies the HTTP status) decides. The
 * mapping never leaks the raw server message or a code to the operator.
 */
export function friendlyAuthError(error: unknown): FriendlyAuthError {
  if (!(error instanceof ApiError)) {
    return friendly("unknown");
  }

  // Prefer a specific server outcome code when present and known.
  const byCode = codeToKey(error.code);
  if (byCode !== null) return friendly(byCode);

  // Otherwise classify by the transport/status kind `apiFetch` already resolved.
  switch (error.kind) {
    case "waking":
      return friendly("waking");
    case "unauthorized":
      // A 401 on an auth POST is a credential/code failure; the opaque message is deliberate.
      return friendly("invalid_credentials");
    case "forbidden":
      return friendly("not_provisioned");
    case "network":
      return friendly("network");
    case "malformed":
      return friendly("malformed");
    case "server":
      return friendly("server");
    case "not_found":
    case "request":
      return friendly("request");
    default:
      return friendly("unknown");
  }
}
